import { Attachment, IAttachment } from '@app/models/Attachment'
import { ISuccessStory, SuccessStory } from '@app/models/SuccessStory'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'
import {
  AssociatedProperty,
  IAssociatedProperty,
} from '@app/models/AssociatedProperty'
import { push } from 'connected-react-router'

export const ATTACHMENTS_IDENTIFIER = 'property-attachment-editor'
export const PHOTOS_IDENTIFIER = 'property-photo-editor'

// -------------------------
// STATE
// -------------------------

export interface ISuccessStoriesEditState {
  readonly canEdit: boolean
  readonly error?: string
  readonly isLoading: boolean
  readonly isSaving: boolean
  readonly loadingPhotos: boolean
  readonly propertyPhotos: List<IAttachment>
  readonly successStory: ISuccessStory
}

const StateRecord = Record({
  canEdit: false,
  error: undefined,
  isLoading: false,
  isSaving: false,
  loadingPhotos: false,
  propertyPhotos: List<IAttachment>(),
  successStory: new SuccessStory(),
})

export class SuccessStoriesEditState extends StateRecord
  implements ISuccessStoriesEditState {
  public static fromJS(initialState?: Partial<ISuccessStoriesEditState>) {
    return new SuccessStoriesEditState(initialState)
  }

  public readonly canEdit: boolean
  public readonly error?: string
  public readonly isLoading: boolean
  public readonly isSaving: boolean
  public readonly loadingPhotos: boolean
  public readonly propertyPhotos: List<IAttachment>
  public readonly successStory: SuccessStory

  constructor(props?: Partial<ISuccessStoriesEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------
export enum ActionTypes {
  ResetState = 'SUCCESS_STORY_EDIT::RESET_STATE',
  DeleteSuccessStory = 'SUCCESS_STORY_EDIT::DELETE_SUCCESS_STORY',
  DeleteSuccessStoryFailure = 'SUCCESS_STORY_EDIT::DELETE_SUCCESS_STORY_FAILURE',
  DeleteSuccessStoryRequest = 'SUCCESS_STORY_EDIT::DELETE_SUCCESS_STORY_REQUEST',
  DeleteSuccessStorySuccess = 'SUCCESS_STORY_EDIT::DELETE_SUCCESS_STORY_SUCCESS',
  SaveSuccessStory = 'SUCCESS_STORY_EDIT::SAVE_SUCCESS_STORY',
  SaveSuccessStoryFailure = 'SUCCESS_STORY_EDIT::SAVE_SUCCESS_STORY_FAILURE',
  SaveSuccessStoryRequest = 'SUCCESS_STORY_EDIT::SAVE_SUCCESS_STORY_REQUEST',
  SaveSuccessStorySuccess = 'SUCCESS_STORY_EDIT::SAVE_SUCCESS_STORY_SUCCESS',
  LoadPropertyPhotos = 'SUCCESS_STORY_EDIT::LOAD_PROPERTY_PHOTOS',
  LoadPropertyPhotosFailure = 'SUCCESS_STORY_EDIT::LOAD_PROPERTY_PHOTOS_FAILURE',
  LoadPropertyPhotosRequest = 'SUCCESS_STORY_EDIT::LOAD_PROPERTY_PHOTOS_REQUEST',
  LoadPropertyPhotosSuccess = 'SUCCESS_STORY_EDIT::LOAD_PROPERTY_PHOTOS_SUCCESS',
  LoadSuccessStory = 'SUCCESS_STORY_EDIT::LOAD_SUCCESS_STORY',
  LoadSuccessStoryFailure = 'SUCCESS_STORY_EDIT::LOAD_SUCCESS_STORY_FAILURE',
  LoadSuccessStoryRequest = 'SUCCESS_STORY_EDIT::LOAD_SUCCESS_STORY_REQUEST',
  LoadSuccessStorySuccess = 'SUCCESS_STORY_EDIT::LOAD_SUCCESS_STORY_SUCCESS',
  SetCanEdit = 'SUCCESS_STORY_EDIT::SET_CAN_EDIT',
  SetAction = 'SUCCESS_STORY_EDIT::SET_ACTION',
  SetChallenge = 'SUCCESS_STORY_EDIT::SET_CHALLENGE',
  SetDescription = 'SUCCESS_STORY_EDIT::SET_DESCRIPTION',
  SetOutline = 'SUCCESS_STORY_EDIT::SET_OUTLINE',
  SetProperty = 'SUCCESS_STORY_EDIT::SET_PROPERTY',
  SetPropertyPhoto = 'SUCCESS_STORY_EDIT::SET_PROPERTY_PHOTO',
  SetResults = 'SUCCESS_STORY_EDIT::SET_RESULTS',
  SetTitle = 'SUCCESS_STORY_EDIT::SET_TITLE',
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).

interface IDeleteSuccessStoryAction {
  successStoryId: string
  type: ActionTypes.DeleteSuccessStory
}

interface IDeleteSuccessStoryRequestAction {
  type: ActionTypes.DeleteSuccessStoryRequest
}

interface IDeleteSuccessStoryFailureAction {
  err: string
  type: ActionTypes.DeleteSuccessStoryFailure
}

interface IDeleteSuccessStorySuccessAction {
  type: ActionTypes.DeleteSuccessStorySuccess
}

interface ILoadPropertyPhotosAction {
  type: ActionTypes.LoadPropertyPhotos
}

interface ILoadPropertyPhotosSuccessAction {
  propertyPhotos: List<IAttachment>
  type: ActionTypes.LoadPropertyPhotosSuccess
}

interface ILoadPropertyPhotosFailureAction {
  err: string
  type: ActionTypes.LoadPropertyPhotosFailure
}

interface ILoadPropertyPhotosRequestAction {
  type: ActionTypes.LoadPropertyPhotosRequest
}

interface ILoadSuccessStoryRequestAction {
  type: ActionTypes.LoadSuccessStoryRequest
}

interface ILoadSuccessStoryAction {
  successStoryId: string
  type: ActionTypes.LoadSuccessStory
}

interface ILoadSuccessStoryFailureAction {
  err: string
  type: ActionTypes.LoadSuccessStoryFailure
}

interface ILoadSuccessStorySuccessAction {
  successStory: ISuccessStory
  type: ActionTypes.LoadSuccessStorySuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveSuccessStoryAction {
  successStory: ISuccessStory
  type: ActionTypes.SaveSuccessStory
}

interface ISaveSuccessStoryFailureAction {
  error: string
  type: ActionTypes.SaveSuccessStoryFailure
}

interface ISaveSuccessStorySuccessAction {
  successStory: ISuccessStory
  type: ActionTypes.SaveSuccessStorySuccess
}

interface ISaveSuccessStoryRequestAction {
  type: ActionTypes.SaveSuccessStoryRequest
}

interface ISetCanEditAction {
  canEdit: boolean
  type: ActionTypes.SetCanEdit
}

interface ISetActionAction {
  action: string
  type: ActionTypes.SetAction
}

interface ISetChallengeAction {
  challenge: string
  type: ActionTypes.SetChallenge
}

interface ISetDescriptionAction {
  description: string
  type: ActionTypes.SetDescription
}

interface ISetOutlineAction {
  outline: string
  type: ActionTypes.SetOutline
}

interface ISetPropertyAction {
  property: IAssociatedProperty
  type: ActionTypes.SetProperty
}

interface ISetPropertyPhotoAction {
  propertyPhoto: IAttachment
  type: ActionTypes.SetPropertyPhoto
}

interface ISetResultsAction {
  results: string
  type: ActionTypes.SetResults
}

interface ISetTitleAction {
  title: string
  type: ActionTypes.SetTitle
}

type KnownAction =
  | IDeleteSuccessStoryFailureAction
  | IDeleteSuccessStoryRequestAction
  | IDeleteSuccessStorySuccessAction
  | ILoadPropertyPhotosFailureAction
  | ILoadPropertyPhotosRequestAction
  | ILoadPropertyPhotosSuccessAction
  | ILoadSuccessStoryFailureAction
  | ILoadSuccessStoryRequestAction
  | ILoadSuccessStorySuccessAction
  | IResetStateAction
  | ISaveSuccessStoryFailureAction
  | ISaveSuccessStoryRequestAction
  | ISaveSuccessStorySuccessAction
  | ISetActionAction
  | ISetCanEditAction
  | ISetChallengeAction
  | ISetDescriptionAction
  | ISetOutlineAction
  | ISetPropertyAction
  | ISetPropertyPhotoAction
  | ISetResultsAction
  | ISetTitleAction

// -------------------------
// ACTION CREATORS
// -------------------------

function deleteSuccessStory(successStoryId: string): IDeleteSuccessStoryAction {
  return { successStoryId, type: ActionTypes.DeleteSuccessStory }
}

function deleteSuccessStoryFailure(
  err: string,
): IDeleteSuccessStoryFailureAction {
  return { err, type: ActionTypes.DeleteSuccessStoryFailure }
}

function deleteSuccessStoryRequest(): IDeleteSuccessStoryRequestAction {
  return { type: ActionTypes.DeleteSuccessStoryRequest }
}

function deleteSuccessStorySuccess(): IDeleteSuccessStorySuccessAction {
  return { type: ActionTypes.DeleteSuccessStorySuccess }
}

function loadPropertyPhotosRequest(): ILoadPropertyPhotosAction {
  return { type: ActionTypes.LoadPropertyPhotos }
}

function loadPropertyPhotosFailure(
  err: string,
): ILoadPropertyPhotosFailureAction {
  return { err, type: ActionTypes.LoadPropertyPhotosFailure }
}

function loadPropertyPhotosSuccess(
  propertyPhotos: List<IAttachment>,
): ILoadPropertyPhotosSuccessAction {
  return { propertyPhotos, type: ActionTypes.LoadPropertyPhotosSuccess }
}

function loadSuccessStory(successStoryId: string): ILoadSuccessStoryAction {
  return { successStoryId, type: ActionTypes.LoadSuccessStory }
}

function loadSuccessStoryRequest(): ILoadSuccessStoryRequestAction {
  return { type: ActionTypes.LoadSuccessStoryRequest }
}

function loadSuccessStoryFailure(err: string): ILoadSuccessStoryFailureAction {
  return { err, type: ActionTypes.LoadSuccessStoryFailure }
}

function loadSuccessStorySuccess(
  successStory: ISuccessStory,
): ILoadSuccessStorySuccessAction {
  return { successStory, type: ActionTypes.LoadSuccessStorySuccess }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function saveSuccessStoryRequest(): ISaveSuccessStoryRequestAction {
  return { type: ActionTypes.SaveSuccessStoryRequest }
}

function saveSuccessStoryFailure(
  error: string,
): ISaveSuccessStoryFailureAction {
  return { error, type: ActionTypes.SaveSuccessStoryFailure }
}

function saveSuccessStorySuccess(
  successStory: ISuccessStory,
): ISaveSuccessStorySuccessAction {
  return {
    successStory,
    type: ActionTypes.SaveSuccessStorySuccess,
  }
}

function save(successStory: SuccessStory): ISaveSuccessStoryAction {
  return {
    successStory,
    type: ActionTypes.SaveSuccessStory,
  }
}

function setCanEdit(canEdit: boolean): ISetCanEditAction {
  return {
    canEdit,
    type: ActionTypes.SetCanEdit,
  }
}

function setAction(action: string): ISetActionAction {
  return {
    action,
    type: ActionTypes.SetAction,
  }
}

function setChallenge(challenge: string): ISetChallengeAction {
  return {
    challenge,
    type: ActionTypes.SetChallenge,
  }
}

function setDescription(description: string): ISetDescriptionAction {
  return {
    description,
    type: ActionTypes.SetDescription,
  }
}

function setOutline(outline: string): ISetOutlineAction {
  return {
    outline,
    type: ActionTypes.SetOutline,
  }
}

function setProperty(property: IAssociatedProperty): ISetPropertyAction {
  return {
    property,
    type: ActionTypes.SetProperty,
  }
}

function setPropertyPhoto(propertyPhoto: IAttachment): ISetPropertyPhotoAction {
  return {
    propertyPhoto,
    type: ActionTypes.SetPropertyPhoto,
  }
}

function setResults(results: string): ISetResultsAction {
  return {
    results,
    type: ActionTypes.SetResults,
  }
}

function setTitle(title: string): ISetTitleAction {
  return {
    title,
    type: ActionTypes.SetTitle,
  }
}

export const actionCreators = {
  resetState,
  deleteSuccessStory,
  save,
  setAction,
  setCanEdit,
  setChallenge,
  setDescription,
  setOutline,
  setProperty,
  setPropertyPhoto,
  setResults,
  setTitle,
  loadSuccessStory,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<SuccessStoriesEditState> = (
  state: SuccessStoriesEditState = new SuccessStoriesEditState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.DeleteSuccessStoryFailure:
      return state
        .set('isLoading', false)
        .set('error', action.err) as SuccessStoriesEditState

    case ActionTypes.DeleteSuccessStoryRequest:
      return state.set('isLoading', true) as SuccessStoriesEditState

    case ActionTypes.DeleteSuccessStorySuccess:
      return state
        .set('isLoading', false)
        .set('successStory', new SuccessStory()) as SuccessStoriesEditState

    case ActionTypes.LoadPropertyPhotosRequest:
      return state.set('loadingPhotos', true) as SuccessStoriesEditState

    case ActionTypes.LoadPropertyPhotosFailure:
      return state
        .set('loadingPhotos', false)
        .set('error', action.err) as SuccessStoriesEditState

    case ActionTypes.LoadPropertyPhotosSuccess:
      return state
        .set('loadingPhotos', false)
        .set('propertyPhotos', action.propertyPhotos) as SuccessStoriesEditState

    case ActionTypes.LoadSuccessStoryFailure:
      return state
        .set('isLoading', false)
        .set('error', action.err) as SuccessStoriesEditState

    case ActionTypes.LoadSuccessStoryRequest:
      return state
        .set('isLoading', true)
        .set('loadingPhotos', true) as SuccessStoriesEditState

    case ActionTypes.LoadSuccessStorySuccess:
      return state
        .set('isLoading', false)
        .set('successStory', action.successStory) as SuccessStoriesEditState

    case ActionTypes.ResetState:
      return new SuccessStoriesEditState()

    case ActionTypes.SaveSuccessStoryFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false)
        .set('isSaving', false) as SuccessStoriesEditState

    case ActionTypes.SaveSuccessStoryRequest:
      return state
        .set('isSaving', true)
        .set('isLoading', true) as SuccessStoriesEditState

    case ActionTypes.SaveSuccessStorySuccess:
      return state
        .set('error', undefined)
        .set('isSaving', false)
        .set('isLoading', false)
        .set('successStory', action.successStory) as SuccessStoriesEditState

    case ActionTypes.SetAction:
      return state.setIn(
        ['successStory', 'action'],
        action.action,
      ) as SuccessStoriesEditState

    case ActionTypes.SetCanEdit:
      return state.set('canEdit', action.canEdit) as SuccessStoriesEditState

    case ActionTypes.SetChallenge:
      return state.setIn(
        ['successStory', 'challenge'],
        action.challenge,
      ) as SuccessStoriesEditState

    case ActionTypes.SetDescription:
      return state.setIn(
        ['successStory', 'description'],
        action.description,
      ) as SuccessStoriesEditState

    case ActionTypes.SetOutline:
      return state.setIn(
        ['successStory', 'outline'],
        action.outline,
      ) as SuccessStoriesEditState

    case ActionTypes.SetProperty:
      return state.setIn(
        ['successStory', 'property'],
        action.property,
      ) as SuccessStoriesEditState

    case ActionTypes.SetPropertyPhoto:
      return state.setIn(
        ['successStory', 'propertyPhoto'],
        action.propertyPhoto,
      ) as SuccessStoriesEditState

    case ActionTypes.SetResults:
      return state.setIn(
        ['successStory', 'results'],
        action.results,
      ) as SuccessStoriesEditState

    case ActionTypes.SetTitle:
      return state.setIn(
        ['successStory', 'title'],
        action.title,
      ) as SuccessStoriesEditState
    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------

function* deleteSuccessStorySaga(action: IDeleteSuccessStoryAction) {
  try {
    yield put(deleteSuccessStoryRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/SuccessStories/${action.successStoryId}`,
      { method: RequestMethod.DELETE },
    )

    if (result.success === true) {
      yield put(push('/properties/successStories'))
      const msg = `Deleted success story`
      yield put(successToast('Success!', msg))
      yield put(deleteSuccessStorySuccess())
    } else {
      yield put(deleteSuccessStoryFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteSuccessStoryFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadPropertyPhotosSaga(action: ISetPropertyAction) {
  try {
    yield put(loadPropertyPhotosRequest())
    const result = yield makeAuthenticatedRequest(
      `/api/Properties/${action.property.propertyId}/Photos`,
    )
    if (result.success === true) {
      yield put(
        loadPropertyPhotosSuccess(
          List(result.results.map((x: any) => Attachment.fromJS(x))),
        ),
      )
    } else {
      yield put(loadSuccessStoryFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPropertyPhotosFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadSuccessStorySaga(action: ILoadSuccessStoryAction) {
  try {
    yield put(loadSuccessStoryRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/SuccessStories/${action.successStoryId}`,
    )

    if (result.success === true) {
      const successStory = SuccessStory.fromJS(result.entity)
      yield put(setProperty(successStory.property))
      yield put(loadSuccessStorySuccess(successStory))
    } else {
      yield put(loadSuccessStoryFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadSuccessStoryFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* saveSuccessStorySaga(action: ISaveSuccessStoryAction) {
  try {
    yield put(saveSuccessStoryRequest())

    let baseUrl = '/api/SuccessStories'

    const successStory = action.successStory
    if (action.successStory.id) {
      baseUrl = baseUrl.concat(`/${action.successStory.id}`)
    }

    const result = yield makeAuthenticatedRequest(baseUrl, {
      body: JSON.stringify(successStory),
      method: action.successStory.id ? RequestMethod.PUT : RequestMethod.POST,
    })

    if (action.successStory.id) {
      const photosResult = yield makeAuthenticatedRequest(
        `/api/Properties/${action.successStory.property.propertyId}/Photos`,
      )
      if (photosResult.success === true) {
        yield put(
          loadPropertyPhotosSuccess(
            List(photosResult.results.map((x: any) => Attachment.fromJS(x))),
          ),
        )
      }
    }

    if (result.success === true) {
      yield put(
        saveSuccessStorySuccess(
          action.successStory.id
            ? action.successStory
            : SuccessStory.fromJS(result.entity),
        ),
      )
      if (result.entity) {
        yield put(
          setProperty(AssociatedProperty.fromJS(result.entity.property)),
        )
      }
      yield put(
        successToast(
          'Success',
          action.successStory.id
            ? `${action.successStory.title}'s information updated`
            : 'New SuccessStory Created',
        ),
      )
      yield put(
        push(
          `/properties/successStory/${
            action.successStory.id ? action.successStory.id : result.entity.id
          }`,
        ),
      )
    } else {
      yield put(saveSuccessStoryFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveSuccessStoryFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeleteSuccessStory() {
  yield takeLatest(ActionTypes.DeleteSuccessStory, deleteSuccessStorySaga)
}

function* watchLoadPropertyPhotos() {
  yield takeLatest(ActionTypes.SetProperty, loadPropertyPhotosSaga)
}

function* watchSaveSuccessStory() {
  yield takeLatest(ActionTypes.SaveSuccessStory, saveSuccessStorySaga)
}

function* watchLoadSuccessStory() {
  yield takeLatest(ActionTypes.LoadSuccessStory, loadSuccessStorySaga)
}

export function* successStoryEditRootSaga() {
  yield fork(watchDeleteSuccessStory)
  yield fork(watchLoadPropertyPhotos)
  yield fork(watchLoadSuccessStory)
  yield fork(watchSaveSuccessStory)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): SuccessStoriesEditState => state.successStoriesEdit

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectCanEdit = () =>
  createSelector(selectModuleState(), (mState) => mState.canEdit)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectIsSaving = () =>
  createSelector(selectModuleState(), (mState) => mState.isSaving)

export const selectLoadingPhotos = () =>
  createSelector(selectModuleState(), (mState) => mState.loadingPhotos)

export const selectPropertyPhotos = () =>
  createSelector(selectModuleState(), (mState) => mState.propertyPhotos)

export const selectSuccessStory = () =>
  createSelector(selectModuleState(), (mState) => mState.successStory)
